import useApp from 'hooks/useApp';
import useData from 'hooks/useData';
import { memo, useEffect, useState } from 'react';
import { useNestedTranslation } from 'hooks/useNestedTranslation';
import ingkaSizes from '@ingka/variables/design-tokens';
import { convertColour } from 'utils/canvas';
import { LoadingWrapper } from 'components/LoadingIndicator/LoadingWrapper';
import { getGapHours } from 'hooks/useGrid/gridFunctionsShared';
import { colourStaticBlack } from '@ingka/variables/colours-css';
import type { TFunction } from 'i18next';
import { getDivisionHoverData } from 'hooks/useGrid/gridFunctionsHeaders';
import { getAlternatingColor } from 'utils/colors';
import { getShortCC } from 'utils/text';
import Toggle from '@ingka/toggle';
import { toIntlLongMonthFullYear } from 'utils/date';
import moment from 'moment';
import SSRIcon from '@ingka/ssr-icon';
import warningTriangle from '@ingka/ssr-icon/paths/warning-triangle';
import type { HoverDataDivision, OrgSelection } from 'types/appContext';
import { getValueOfChecked } from 'hooks/useOrgSelection/useOrgSelection.utils';
import { roundDisplayValueDoubleDecimal } from 'utils/number';
import Text from '@ingka/text';
import Graph from './Graph';
import GraphHeader from './GraphHeader';
import { ChartDataWithLabel, ChartGeneratorOptions, GraphData } from './types';
import GraphLegend from './GraphLegend';
import classes from './Graph.module.scss';

const graphOptions = (
    t: TFunction<'translation', undefined, 'translation'>,
    graphData: GraphData,
): ChartGeneratorOptions => (
    {
        layout: {
            padleft: graphData.datasets.length && graphData.datasets[0].data.length ? 0 : 20,
        },
        scales: {
            tickColor: convertColour(colourStaticBlack),
            weeksThreshold: 16,
            labels: graphData.labels,
            hasNegativeValues: graphData.datasets.some(ds => ds.data.some(d => d < 0)),
            formatterYTicks: roundDisplayValueDoubleDecimal
        },
        plugins: {
            customYLabel: {
                text: t('hours'),
                color: convertColour(colourStaticBlack),
            },
            emptyGraph: {
                text: t('no_data')
            },
            tooltip: {
                external: {
                    tooltipDivId: 'gap-distribution-tooltip',
                    showAbovePointer: false,
                    titleFormatter: (currentTitle: string) => (
                        `${t('week')} ${currentTitle.slice(-2)} (${toIntlLongMonthFullYear(moment(currentTitle).toDate())})`
                    ),
                    numberFormatter: roundDisplayValueDoubleDecimal,
                },
            }
        }

    }
);

const emptyDataset = (t: TFunction<'translation', undefined, 'translation'>) => [{
    label: t('no_data'),
    data: [],
}];

const datasetIsEmpty = (data: ChartDataWithLabel[]) => data.length === 1 && data[0].data.length === 0;

export const getDivisionLabel = (divisionCode: string, hierarchy: OrgSelection['utils']['hierarchy']): string => {
    if (divisionCode.includes(' & ')) {
        const divisions = divisionCode.split(' & ');

        return divisions.map(division => getDivisionLabel(division, hierarchy)).join(' & ');
    }
    if (!hierarchy?.divisionHierarchy) {
        return divisionCode;
    }

    return hierarchy.divisionHierarchy.find(({ division }) => division?.code === divisionCode)?.division?.name ?? divisionCode;
};

export const GapDistributionGraph = memo(() => {
    const t = useNestedTranslation('graph');
    const { gridData, timeSelection, orgSelection } = useApp();
    const { config, currentScenario, isFetching, currentUnit, currentUnitType, orgData } = useData();
    const [toggleIndex, setToggleIndex] = useState<number>(0); // 0 means divisions(=BF), 1 means cost centres
    const [warningText, setWarningText] = useState<string | null>(null); // 0 means divisions(=BF), 1 means cost centres
    const [graphData, setGraphData] = useState<GraphData>({
        datasets: emptyDataset(t.global),
        labels: timeSelection.timeArray,
    });

    useEffect(() => {
        if (!gridData?.hoverDataPerCostCentre?.length || !currentUnit || !currentUnitType || !orgSelection || !orgData) {
            setGraphData({
                datasets: emptyDataset(t.global),
                labels: timeSelection.timeArray,
            });
            setWarningText(null);

            return;
        }
        const checkedDivisions = getValueOfChecked(orgSelection.selections.divisionSelection ?? [], 'value');
        const divisionData = toggleIndex === 0
            ? getDivisionHoverData(gridData.hoverDataPerCostCentre, currentUnit, currentUnitType, orgData)
            : null;

        const data = (toggleIndex === 0 && divisionData)
            ? divisionData.data.filter((division: HoverDataDivision) => {
                if (checkedDivisions.includes(division.divisionId)) return true;

                return false;
            })
            : gridData.hoverDataPerCostCentre;

        if (toggleIndex === 0 && divisionData && divisionData.warningDoubleCounted.length > 0) {
            setWarningText(`${t('attention_multiple_business_functions')}: ${
                divisionData.warningDoubleCounted.length === 1
                    ? getShortCC(divisionData.warningDoubleCounted[0])
                    : divisionData.warningDoubleCounted.map(longCC => getShortCC(longCC)).join(', ')
            }`);
        } else {
            setWarningText(null);
        }

        const newDataSets: ChartDataWithLabel[] = data.map((entity, index) => ({
            type: 'line' as const,
            label: 'costCentre' in entity ? `CC ${getShortCC(entity.costCentre)}` : getDivisionLabel(entity.divisionId, orgSelection.utils.hierarchy),
            data: entity.hoverData.map(el => getGapHours(el.gap)),
            backgroundColor: getAlternatingColor(index),
            spanGaps: true,
            segment: {
                borderColor: getAlternatingColor(index),
                backgroundColor: getAlternatingColor(index),
                borderWidth: 2, // px
            },
            pointRadius: 0, // px
            borderColor: getAlternatingColor(index),
        }));

        setGraphData({
            datasets: newDataSets,
            labels: timeSelection.timeArray
        });
    }, [gridData, config, currentScenario, t, timeSelection.timeArray, currentUnit, currentUnitType, orgSelection, toggleIndex, orgData]);

    return (
        <LoadingWrapper
            className={classes['loading-wrapper-status']}
            isLoading={isFetching}
        >
            <>
                <div className={classes['status-header-container']}>
                    <GraphHeader
                        headerTitle={t('gap_distribution_title')}
                        headerSubTitle={t('gap_distribution_subtitle')}
                    />
                    <Toggle
                        buttons={[{ text: t('business_functions') }, { text: t('cost_centres') }]}
                        fluid={false}
                        activeIndex={toggleIndex ?? 0}
                        onClick={(event, index) => {
                            event.preventDefault();
                            setToggleIndex(index);
                        }}
                    />
                </div>
                <Graph
                    graphData={graphData}
                    graphOptions={graphOptions(t.global, graphData)}
                    customOptions={{ height: ingkaSizes.space1350 }}
                />
                {!datasetIsEmpty(graphData.datasets) && (
                    <GraphLegend
                        legends={graphData.datasets.map(el => ({
                            label: el.label,
                            color: el.backgroundColor as string
                        }))}
                    />
                )}
                {!!warningText && (
                    <div className={classes['warning-message']}>
                        <SSRIcon className={classes['warning-message-icon']} paths={warningTriangle} data-testid="status-graph-warning" />
                        <Text textStyle="Caption.S" data-testid="status-graph-warning-text">{warningText}</Text>
                    </div>
                )}
            </>
        </LoadingWrapper>
    );
});
